import { useMemo, useState } from 'react'

import { CompositionPie } from './CompositionPie'
import { KpiTiles } from './KpiTiles'
import { OfficerLeaderboardRow } from './OfficerLeaderboardRow'
import { ReportTable } from './reports/ReportTable'
import { ShowMoreButton } from './reports/ShowMoreButton'
import { TrendCard } from './TrendCard'
import { useOfficerPerformance } from '../../hooks/useOfficerPerformance'
import { officerTilesForMandal, tilesFor } from '../../lib/aggregate'
import { bandPredicate, officerBandSegments, OFF_BUCKETS } from '../../lib/composition'
import { LENSES } from '../../lib/lens'
import { officerRollup } from '../../lib/officers'

import type { GisOverview, GisScope } from '../../api/types'

type LeaderMode = 'worst' | 'top'

const pct = (v: number | null): string => (v === null ? '—' : `${v}%`)

interface OfficersTabContentProps {
  overview: GisOverview
  scope: GisScope
  activeMandals: readonly string[]
  onSelectOfficer: (id: string, name: string) => void
}

/**
 * District **Officers** lens (V4) — leads with the completion-band composition pie (On-track / Watch /
 * Below 60, centre = avg completion %); clicking a band filters the leaderboard to officers in it. Below:
 * the non-redundant KPI tiles and the accountability leaderboard (Needs-support / Top toggle).
 */
export function OfficersTabContent({ overview, scope, activeMandals, onSelectOfficer }: OfficersTabContentProps) {
  const L = LENSES.officers
  const [mode, setMode] = useState<LeaderMode>('worst')
  const [activeBand, setActiveBand] = useState('')
  // The server-paginated leaderboard takes a single mandal, so it's scoped only when exactly one is
  // selected; 2+ falls back to the district leaderboard (no client-side union for this feed).
  const singleMandal = activeMandals.length === 1 ? activeMandals[0] : undefined
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useOfficerPerformance({ scope, mandal: singleMandal })

  // When exactly one mandal is selected (and present), scope the band-pie centre + KPI tiles to its row.
  const mandalRow = singleMandal ? (overview.mandals.find((m) => m.name === singleMandal) ?? null) : null
  const avgCompletion = mandalRow ? mandalRow.officerAvgCompletion : overview.summary.officerAvgCompletion
  const officerTiles = mandalRow ? officerTilesForMandal(mandalRow) : tilesFor('officers', overview)

  const rollup = useMemo(() => {
    const rows = data?.pages.flatMap((p) => p.rows) ?? []
    return officerRollup(rows).filter((o) => o.assigned > 0)
  }, [data?.pages])
  const worst = useMemo(() => [...rollup].sort((a, b) => (a.completionPct ?? 0) - (b.completionPct ?? 0))[0], [rollup])
  const sorted = useMemo(
    () => [...rollup].sort((a, b) => ((a.completionPct ?? 0) - (b.completionPct ?? 0)) * (mode === 'worst' ? 1 : -1)),
    [rollup, mode],
  )
  const segments = useMemo(() => officerBandSegments(rollup), [rollup])
  const bandTest = bandPredicate(OFF_BUCKETS, activeBand)
  const activeLabel = segments.find((s) => s.key === activeBand)?.label
  const shown = useMemo(
    () => (bandTest ? sorted.filter((o) => o.completionPct !== null && bandTest(o.completionPct)) : sorted),
    [sorted, bandTest],
  )

  return (
    <>
      {/* ===== Version 1 – Card Style (preserved — uncomment + re-import HeroCard/heroDataFor/deltaBadgeText/hexA to restore) =====
      <HeroCard
        hero={heroDataFor('officers', overview)}
        gradient={`linear-gradient(135deg, ${L.accent}, ${hexA(L.accent, 0.72)})`}
        badge={deltaBadgeText('officers', overview.summary.deltas.officers)}
      />
      ===== end Version 1 ===== */}

      <div className="mb-2 flex items-center gap-3 rounded-[10px] border border-slate-900/5 p-3">
        <CompositionPie
          segments={segments}
          total={rollup.length}
          center={{ value: pct(avgCompletion), label: 'Avg' }}
          active={activeBand}
          onToggle={(k) => setActiveBand(activeBand === k ? '' : k)}
        />
        {worst && (
          <p className="flex-1 text-[12px] leading-relaxed text-slate-700">
            Escalate <b>{worst.name}</b> — {pct(worst.completionPct)} completion, <b>{worst.missed} missed</b> of {worst.assigned} assigned.
          </p>
        )}
      </div>

      <div className="mt-3">
        <KpiTiles tiles={officerTiles} />
      </div>

      <div className="text-muted-text mx-0.5 mt-4 mb-2 flex items-center justify-between text-[10px] font-extrabold tracking-wider uppercase">
        Officer leaderboard
        <span className="flex gap-0.5 rounded-md border border-slate-900/10 bg-slate-50 p-0.5">
          {(['worst', 'top'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded px-2 py-1 text-[10.5px] font-bold normal-case ${mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              {m === 'worst' ? 'Needs support' : 'Top'}
            </button>
          ))}
        </span>
      </div>

      {activeLabel && (
        <div className="mx-0.5 mb-2 flex items-center gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 font-semibold text-white">
            {activeLabel}
            <button type="button" onClick={() => setActiveBand('')} aria-label="Clear band filter" className="ml-0.5 grid h-4 w-4 place-items-center rounded-full hover:bg-white/20">×</button>
          </span>
        </div>
      )}

      <ReportTable
        title="Officers"
        isPending={isPending}
        isError={isError}
        isEmpty={shown.length === 0}
        emptyLabel="No officers match this band in this period."
        onRetry={() => void refetch()}
        footer={hasNextPage ? <ShowMoreButton onClick={() => void fetchNextPage()} loading={isFetchingNextPage} /> : undefined}
      >
        <div>
          {shown.map((o) => (
            <OfficerLeaderboardRow
              key={o.officerId}
              name={o.name}
              subtitle={`${o.mandals.length} mandal${o.mandals.length === 1 ? '' : 's'}`}
              completed={o.completed}
              assigned={o.assigned}
              missed={o.missed}
              completionPct={o.completionPct}
              onClick={() => onSelectOfficer(o.officerId, o.name)}
            />
          ))}
        </div>
      </ReportTable>

      {!mandalRow && (
        <TrendCard values={overview.trends.officers} buckets={overview.trendBuckets} bucketSize={overview.trendBucketSize} accent={L.accent} />
      )}
    </>
  )
}
